import { BlobReader, TextReader, Uint8ArrayReader, Uint8ArrayWriter, ZipWriter, } from '@zip.js/zip.js';
import { mergeOptions } from "../Options.js";
import { toIumSourceToPath } from "../transformation/source_zip.js";
import { shouldAvoidCompression } from "../utilities/should_avoid_compression.js";
import { CURRENT_IUM_VERSION } from "./versions/index.js";
/**
 * This method will zip a file collection and return the zip as an ArrayBuffer
 * @param fileCollection - The file collection to zip
 * @param options - Options for the zip process
 * @returns Zip as an Uint8Array
 */
export async function toIum(fileCollection, options = {}) {
    const { includeData = true, getExtraFiles, mimetype = 'application/x-ium+zip', } = options;
    const zipWriter = new ZipWriter(new Uint8ArrayWriter());
    await zipWriter.add('mimetype', new TextReader(mimetype), {
        compressionMethod: 0,
        dataDescriptor: false, // ensures the data length is written in the local file header
        extendedTimestamp: false, // smaller payload, the extra field is empty
    });
    const sources = [];
    const paths = {};
    const promises = [];
    for (const source of fileCollection.sources) {
        const newSource = {
            uuid: source.uuid,
            relativePath: source.relativePath,
            originalRelativePath: source.originalRelativePath,
            baseURL: source.baseURL,
            lastModified: source.lastModified,
            size: source.size,
            extra: source.extra,
            options: source.options,
        };
        sources.push(newSource);
        const shouldIncludeFile = includeData || source.baseURL === 'ium:/';
        if (!shouldIncludeFile)
            continue;
        newSource.baseURL = 'ium:/';
        const pathname = toIumSourceToPath(newSource);
        const addOptions = shouldAvoidCompression(source) ? { compressionMethod: 0 } : undefined;
        promises.push(zipWriter.add(pathname, source.stream(), addOptions));
        paths[source.uuid] = pathname;
    }
    await Promise.all(promises);
    const index = {
        version: CURRENT_IUM_VERSION,
        options: fileCollection.options,
        sources,
        paths,
    };
    if (getExtraFiles) {
        await Promise.all(map(getExtraFiles(structuredClone(index), fileCollection), async (extraFile) => {
            const { relativePath, data, uuid = crypto.randomUUID() } = extraFile;
            const source = {
                uuid,
                extra: true,
                baseURL: 'ium:/',
                relativePath,
                options: extraFile.options
                    ? mergeOptions(fileCollection.options, extraFile.options)
                    : fileCollection.options,
            };
            sources.push(source);
            const pathname = toIumSourceToPath(source);
            const addOptions = shouldAvoidCompression(source)
                ? { compressionMethod: 0 }
                : undefined;
            const fileReader = getExtraFileContentReader(data);
            await zipWriter.add(pathname, fileReader, addOptions);
            paths[uuid] = pathname;
        }));
    }
    await zipWriter.add('index.json', new TextReader(JSON.stringify(index, null, 2)));
    return await zipWriter.close();
}
export const UNSUPPORTED_EXTRA_FILE_CONTENT_ERROR = `Unsupported file content type.
If you passed a Node.js Stream convert it to Web Stream.`;
function getExtraFileContentReader(fileContent) {
    if (typeof fileContent === 'string') {
        return new TextReader(fileContent);
    }
    else if (fileContent instanceof Uint8Array) {
        // support Node.js Buffer they extend Uint8Array,
        // but when it is read by Uint8ArrayReader it produces `Error: Split zip file`
        // wrap Node.js Buffer into a new Uint8Array fix the issue
        return new Uint8ArrayReader(new Uint8Array(fileContent.buffer, fileContent.byteOffset, fileContent.length));
    }
    else if (fileContent instanceof ArrayBuffer) {
        return new Uint8ArrayReader(new Uint8Array(fileContent));
    }
    else if (fileContent instanceof Blob) {
        return new BlobReader(fileContent);
    }
    else if (fileContent instanceof ReadableStream) {
        return fileContent;
    }
    throw new Error(UNSUPPORTED_EXTRA_FILE_CONTENT_ERROR);
}
function* map(items, mapFn) {
    for (const item of items) {
        yield mapFn(item);
    }
}
//# sourceMappingURL=to_ium.js.map